import React from 'react';

import { DomainEventType } from '@/services/api/matterChangesService';

import { ArtifactCreatedEvent } from './ArtifactCreatedEvent';
import { ArtifactDigestCreatedEvent } from './ArtifactDigestCreatedEvent';
import { ArtifactDigestStatusUpdatedEvent } from './ArtifactDigestStatusUpdatedEvent';
import { BlobCreatedEvent } from './BlobCreatedEvent';
import { BlobDeletedEvent } from './BlobDeletedEvent';
import { BlobTransformCompletedEvent } from './BlobTransformCompletedEvent';
import { CoverageCategoryChangedEvent } from './CoverageCategoryChangedEvent';
import { CoverageCreatedEvent } from './CoverageCreatedEvent';
import { CoverageStatusChangedEvent } from './CoverageStatusChangedEvent';
import { CoverageUpdatedEvent } from './CoverageUpdatedEvent';
import { CoverageUpdatedEventV2 } from './CoverageUpdatedEventV2';
import { DeliverableCreatedEvent } from './DeliverableCreatedEvent';
import { DeliverableMatchApprovedEvent } from './DeliverableMatchApprovedEvent';
import { DeliverableMatchCreatedEvent } from './DeliverableMatchCreatedEvent';
import { DeliverableMatchStatusChangedEvent } from './DeliverableMatchStatusChangedEvent';
import { DeliverableSatisfiedEvent } from './DeliverableSatisfiedEvent';
import { DeliverableStatusChangedEvent } from './DeliverableStatusChangedEvent';
import { DocumentCreatedEvent } from './DocumentCreatedEvent';
import { DocumentDeletedEvent } from './DocumentDeletedEvent';
import { DocumentHipaaPackageGenerationAuditEvent } from './DocumentHipaaPackageGenerationAuditEvent';
import { DocumentLoRGenerationAuditEvent } from './DocumentLoRGenerationAuditEvent';
import { DocumentStatusChangedEvent } from './DocumentStatusChangedEvent';
import { IncidentCreatedEvent } from './IncidentCreatedEvent';
import { IncidentUpdatedEvent } from './IncidentUpdatedEvent';
import { MatterClientCreatedEvent } from './MatterClientCreatedEvent';
import { MatterClientPhoneNumberAddedEvent } from './MatterClientPhoneNumberAddedEvent';
import { MatterClientUpdatedEvent } from './MatterClientUpdatedEvent';
import { MatterClientUpdatedEventV2 } from './MatterClientUpdatedEventV2';
import { MatterCreatedEvent } from './MatterCreatedEvent';
import { MatterInvestigationCreatedEvent } from './MatterInvestigationCreatedEvent';
import { MatterInvestigationUpdatedEvent } from './MatterInvestigationUpdatedEvent';
import { MatterInvestigationUpdatedEventV2 } from './MatterInvestigationUpdatedEventV2';
import { MatterRetainerSignedByAttorneyEvent } from './MatterRetainerSignedByAttorneyEvent';
import { MatterRetainerSignedByClientEvent } from './MatterRetainerSignedByClientEvent';
import { MatterRetainerSignedEvent } from './MatterRetainerSignedEvent';
import { MatterStatusChangedEvent } from './MatterStatusChangedEvent';
import { MatterUpdatedEvent } from './MatterUpdatedEvent';
import { MatterUpdatedEventV2 } from './MatterUpdatedEventV2';
import { PoliceReportCreatedEvent } from './PoliceReportCreatedEvent';
import { PoliceReportStatusChangedEvent } from './PoliceReportStatusChangedEvent';
import { PoliceReportUpdatedEvent } from './PoliceReportUpdatedEvent';
import { PoliceReportUpdatedEventV2 } from './PoliceReportUpdatedEventV2';
import { ProviderCreatedEvent } from './ProviderCreatedEvent';
import { ProviderCuratedEvent } from './ProviderCuratedEvent';
import { ProvidersMergedEvent } from './ProvidersMergedEvent';
import { ProviderStatusChangedEvent } from './ProviderStatusChangedEvent';
import { ProviderTreatmentStatusChangedEvent } from './ProviderTreatmentStatusChangedEvent';
import { StaffNoteCreatedEvent } from './StaffNoteCreatedEvent';
import { StaffNoteDeletedEvent } from './StaffNoteDeletedEvent';
import { StaffNoteUpdatedEvent } from './StaffNoteUpdatedEvent';
import { StaffNoteUpdatedEventV2 } from './StaffNoteUpdatedEventV2';
import { TaskAssignedEvent } from './TaskAssignedEvent';
import { TaskCreatedEvent } from './TaskCreatedEvent';
import { TaskDeletedEvent } from './TaskDeletedEvent';
import { TaskDueDateChangedEvent } from './TaskDueDateChangedEvent';
import { TaskStatusChangedEvent } from './TaskStatusChangedEvent';
import { TaskUpdateCreatedEvent } from './TaskUpdateCreatedEvent';
import { TaskUpdateDeletedEvent } from './TaskUpdateDeletedEvent';
import { TaskUpdateUpdatedEvent } from './TaskUpdateUpdatedEvent';
import { TaskUpdateUpdatedEventV2 } from './TaskUpdateUpdatedEventV2';
import { TreatmentCheckInSubmittedEvent } from './TreatmentCheckInSubmittedEvent';
import { TreatmentRequestCreatedEvent } from './TreatmentRequestCreatedEvent';
import { TreatmentRequestDeletedEvent } from './TreatmentRequestDeletedEvent';
import { TreatmentRequestResearchCompletedEvent } from './TreatmentRequestResearchCompletedEvent';
import { TreatmentRequestStatusChangedEvent } from './TreatmentRequestStatusChangedEvent';
import { TreatmentRequestUpdatedEvent } from './TreatmentRequestUpdatedEvent';
import { TreatmentRequestUpdatedEventV2 } from './TreatmentRequestUpdatedEventV2';
import { VoiceAgentRunUnblockedEvent } from './VoiceAgentRunUnblockedEvent';
import { VoiceAiCallCompletedEvent } from './VoiceAiCallCompletedEvent';

export interface EventRendererProps {
  event: DomainEventType;
}

/** Check if an event type has a dedicated renderer */
export const isEventSupported = (event: DomainEventType): boolean => {
  switch (event.event_code) {
    case 'artifact_created':
    case 'artifact_digest_created':
    case 'artifact_digest_status_updated':
    case 'blob_created':
    case 'blob_deleted':
    case 'blob_transform_completed':
    case 'coverage_category_changed':
    case 'coverage_created':
    case 'coverage_status_changed':
    case 'coverage_updated':
    case 'deliverable_created':
    case 'deliverable_match_approved':
    case 'deliverable_match_created':
    case 'deliverable_match_status_changed':
    case 'deliverable_satisfied':
    case 'deliverable_status_changed':
    case 'document_created':
    case 'document_deleted':
    case 'document_hipaa_package_generation_audit':
    case 'document_lor_generation_audit':
    case 'document_status_changed':
    case 'incident_created':
    case 'incident_updated':
    case 'matter_client_created':
    case 'matter_client_phone_number_added':
    case 'matter_client_updated':
    case 'matter_created':
    case 'matter_investigation_created':
    case 'matter_investigation_updated':
    case 'matter_retainer_signed_by_attorney':
    case 'matter_retainer_signed_by_client':
    case 'matter_retainer_signed':
    case 'matter_status_changed':
    case 'matter_updated':
    case 'police_report_created':
    case 'police_report_status_changed':
    case 'police_report_updated':
    case 'provider_created':
    case 'provider_curated':
    case 'providers_merged':
    case 'provider_status_changed':
    case 'provider_treatment_status_changed':
    case 'staff_note_created':
    case 'staff_note_deleted':
    case 'staff_note_updated':
    case 'task_assigned':
    case 'task_created':
    case 'task_deleted':
    case 'task_due_date_changed' as DomainEventType['event_code']:
    case 'task_status_changed':
    case 'task_update_created':
    case 'task_update_deleted':
    case 'task_update_updated':
    case 'treatment_check_in_submitted':
    case 'treatment_request_created':
    case 'treatment_request_deleted':
    case 'treatment_request_research_completed':
    case 'treatment_request_status_changed':
    case 'treatment_request_updated':
    case 'voice_ai_run_unblocked':
    case 'voice_ai_call_completed':
      return true;
    default:
      return false;
  }
};

export const EventRenderer: React.FC<EventRendererProps> = ({ event }) => {
  switch (event.event_code) {
    // Artifact events
    case 'artifact_created':
      return <ArtifactCreatedEvent event={event} />;
    case 'artifact_digest_created':
      return <ArtifactDigestCreatedEvent event={event} />;
    case 'artifact_digest_status_updated':
      return <ArtifactDigestStatusUpdatedEvent event={event} />;

    // Blob events
    case 'blob_created':
      return <BlobCreatedEvent event={event} />;
    case 'blob_deleted':
      return <BlobDeletedEvent event={event} />;
    case 'blob_transform_completed':
      return <BlobTransformCompletedEvent event={event} />;

    // Coverage events
    case 'coverage_category_changed':
      return <CoverageCategoryChangedEvent event={event} />;
    case 'coverage_created':
      return <CoverageCreatedEvent event={event} />;
    case 'coverage_status_changed':
      return <CoverageStatusChangedEvent event={event} />;
    case 'coverage_updated':
      // Use V2 renderer if event has previous field (new format)
      return event.payload.previous !== undefined ? (
        <CoverageUpdatedEventV2 event={event} />
      ) : (
        <CoverageUpdatedEvent event={event} />
      );

    // Deliverable events
    case 'deliverable_created':
      return <DeliverableCreatedEvent event={event} />;
    case 'deliverable_match_approved':
      return <DeliverableMatchApprovedEvent event={event} />;
    case 'deliverable_match_created':
      return <DeliverableMatchCreatedEvent event={event} />;
    case 'deliverable_match_status_changed':
      return <DeliverableMatchStatusChangedEvent event={event} />;
    case 'deliverable_satisfied':
      return <DeliverableSatisfiedEvent event={event} />;
    case 'deliverable_status_changed':
      return <DeliverableStatusChangedEvent event={event} />;

    // Document events
    case 'document_created':
      return <DocumentCreatedEvent event={event} />;
    case 'document_deleted':
      return <DocumentDeletedEvent event={event} />;
    case 'document_hipaa_package_generation_audit':
      return <DocumentHipaaPackageGenerationAuditEvent event={event} />;
    case 'document_lor_generation_audit':
      return <DocumentLoRGenerationAuditEvent event={event} />;
    case 'document_status_changed':
      return <DocumentStatusChangedEvent event={event} />;

    // Incident events
    case 'incident_created':
      return <IncidentCreatedEvent event={event} />;
    case 'incident_updated':
      return <IncidentUpdatedEvent event={event} />;

    // Matter Client events
    case 'matter_client_created':
      return <MatterClientCreatedEvent event={event} />;
    case 'matter_client_phone_number_added':
      return <MatterClientPhoneNumberAddedEvent event={event} />;
    case 'matter_client_updated':
      // Use V2 renderer if event has previous field (new format)
      return event.payload.previous !== undefined ? (
        <MatterClientUpdatedEventV2 event={event} />
      ) : (
        <MatterClientUpdatedEvent event={event} />
      );

    // Matter events
    case 'matter_created':
      return <MatterCreatedEvent event={event} />;
    case 'matter_investigation_created':
      return <MatterInvestigationCreatedEvent event={event} />;
    case 'matter_investigation_updated':
      // Use V2 renderer if event has previous field (new format)
      return event.payload.previous !== undefined ? (
        <MatterInvestigationUpdatedEventV2 event={event} />
      ) : (
        <MatterInvestigationUpdatedEvent event={event} />
      );
    case 'matter_retainer_signed_by_attorney':
      return <MatterRetainerSignedByAttorneyEvent event={event} />;
    case 'matter_retainer_signed_by_client':
      return <MatterRetainerSignedByClientEvent event={event} />;
    case 'matter_retainer_signed':
      return <MatterRetainerSignedEvent event={event} />;
    case 'matter_status_changed':
      return <MatterStatusChangedEvent event={event} />;
    case 'matter_updated':
      // Use V2 renderer if event has previous field (new format)
      return event.payload.previous !== undefined ? (
        <MatterUpdatedEventV2 event={event} />
      ) : (
        <MatterUpdatedEvent event={event} />
      );

    // Police Report events
    case 'police_report_created':
      return <PoliceReportCreatedEvent event={event} />;
    case 'police_report_status_changed':
      return <PoliceReportStatusChangedEvent event={event} />;
    case 'police_report_updated':
      // Use V2 renderer if event has previous field (new format)
      return event.payload.previous !== undefined ? (
        <PoliceReportUpdatedEventV2 event={event} />
      ) : (
        <PoliceReportUpdatedEvent event={event} />
      );

    // Provider events
    case 'provider_created':
      return <ProviderCreatedEvent event={event} />;
    case 'provider_curated':
      return <ProviderCuratedEvent event={event} />;
    case 'providers_merged':
      return <ProvidersMergedEvent event={event} />;
    case 'provider_status_changed':
      return <ProviderStatusChangedEvent event={event} />;
    case 'provider_treatment_status_changed':
      return <ProviderTreatmentStatusChangedEvent event={event} />;

    // Staff note events
    case 'staff_note_created':
      return <StaffNoteCreatedEvent event={event} />;
    case 'staff_note_deleted':
      return <StaffNoteDeletedEvent event={event} />;
    case 'staff_note_updated':
      // Use V2 renderer if event has previous field (new format)
      return event.payload.previous !== undefined ? (
        <StaffNoteUpdatedEventV2 event={event} />
      ) : (
        <StaffNoteUpdatedEvent event={event} />
      );

    // Task events
    case 'task_assigned':
      return <TaskAssignedEvent event={event} />;
    case 'task_created':
      return <TaskCreatedEvent event={event} />;
    case 'task_deleted':
      return <TaskDeletedEvent event={event} />;
    case 'task_due_date_changed' as DomainEventType['event_code']:
      // TODO: Remove type assertion after running `pnpm run generate:api`
      // The generated API client is out of sync with the backend schema.
      // Regenerating now would require fixing ~100 type errors across unrelated files
      // due to accumulated drift. This should be done in a dedicated PR.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return <TaskDueDateChangedEvent event={event as any} />;
    case 'task_status_changed':
      return <TaskStatusChangedEvent event={event} />;
    case 'task_update_created':
      return <TaskUpdateCreatedEvent event={event} />;
    case 'task_update_deleted':
      return <TaskUpdateDeletedEvent event={event} />;
    case 'task_update_updated':
      // Use V2 renderer if event has previous field (new format)
      return event.payload.previous !== undefined ? (
        <TaskUpdateUpdatedEventV2 event={event} />
      ) : (
        <TaskUpdateUpdatedEvent event={event} />
      );

    // Treatment events
    case 'treatment_check_in_submitted':
      return <TreatmentCheckInSubmittedEvent event={event} />;
    case 'treatment_request_created':
      return <TreatmentRequestCreatedEvent event={event} />;
    case 'treatment_request_deleted':
      return <TreatmentRequestDeletedEvent event={event} />;
    case 'treatment_request_research_completed':
      return <TreatmentRequestResearchCompletedEvent event={event} />;
    case 'treatment_request_status_changed':
      return <TreatmentRequestStatusChangedEvent event={event} />;
    case 'treatment_request_updated':
      // Use V2 renderer if event is version 2 and has previous field
      if (event.version === 2 && event.payload.previous !== undefined) {
        return <TreatmentRequestUpdatedEventV2 event={event} />;
      }
      return <TreatmentRequestUpdatedEvent event={event} />;

    // Voice AI events
    case 'voice_ai_run_unblocked':
      return <VoiceAgentRunUnblockedEvent event={event} />;
    case 'voice_ai_call_completed':
      return <VoiceAiCallCompletedEvent event={event} />;

    default:
      return null;
  }
};
